import React, { useEffect, useRef, useState } from "react";
import { Dropdown, FloatingLabel, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import moment from "moment";

interface TimePickerProps {
  selectedTime?: string;
  onChange: (time: string) => void;
  label?: string;
  disabled?: boolean;
  minTime?: string;
  required?: boolean;
  date?: any;
}

const generateTimeOptions = () => {
  const times: string[] = [];
  for (let hour = 1; hour <= 12; hour++) {
    for (let minutes of ["00", "30"]) {
      const formattedHour = hour.toString().padStart(2, "0");
      times.push(`${formattedHour}:${minutes}`);
    }
  }
  return times;
};

const TimePicker: React.FC<TimePickerProps> = ({
  selectedTime = "",
  onChange,
  label = "Select Time",
  disabled = false,
  minTime = "",
  required = false,
  date = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [period, setPeriod] = useState<"AM" | "PM">("AM");
  const hasUserInput = useRef(false);
  const newDate = moment(date).format("YYYY-MM-DD");
  const currentDate = moment(new Date()).format("YYYY-MM-DD");
  const isSameDate = newDate === currentDate;

  useEffect(() => {
    if (selectedTime) {
      const m = moment(selectedTime, "HH:mm");
      setInputValue(m.format("hh:mm"));
      setPeriod(m.format("A") as "AM" | "PM");
      hasUserInput.current = false;
    } else {
      if (isSameDate && !hasUserInput.current) {
        const now = moment();
        setInputValue(now.format("hh:mm"));
        setPeriod(now.format("A") as "AM" | "PM");
        onChange(now.format("HH:mm"));
      } else if (!hasUserInput.current) {
        setInputValue("");
        setPeriod("AM");
        onChange("");
      }
    }
  }, [selectedTime, date]);

  // Format incomplete time input
  const formatIncompleteTime = (value: string): string => {
    if (!value) return "";

    // Remove all non-digit characters
    let digits = value.replace(/[^0-9]/g, "");

    if (digits.length === 0) return "";

    // Handle single digit (e.g., "1" → "01:00")
    if (digits.length === 1) {
      const hour = parseInt(digits, 10);
      if (hour > 1) return `0${hour}:00`;
      return `${digits.padStart(2, "0")}:00`;
    }

    // Handle two digits (e.g., "12" → "12:00")
    if (digits.length === 2) {
      const hour = parseInt(digits, 10);
      if (hour > 12) return "12:00";
      return `${digits.padStart(2, "0")}:00`;
    }

    // Handle three digits (e.g., "123" → "12:30")
    if (digits.length === 3) {
      const hour = digits.substring(0, 2);
      const minuteDigit = digits.substring(2, 3);
      const minutes = minuteDigit === "0" ? "00" : `${minuteDigit}0`;
      return `${hour}:${minutes}`;
    }

    // Handle four or more digits (e.g., "1234" → "12:34")
    if (digits.length >= 4) {
      const hour = digits.substring(0, 2);
      let minutes = digits.substring(2, 4);

      // Validate minutes
      const minutesNum = parseInt(minutes, 10);
      if (minutesNum > 59) minutes = "59";

      return `${hour}:${minutes}`;
    }

    return value;
  };

  // Validate time against current time if same date
  const validateTime = (time: string, period: "AM" | "PM") => {
    if (!time || time.length !== 5) return { isValid: false, time24: "" };

    const m = moment(`${time} ${period}`, "hh:mm A");
    const time24 = m.format("HH:mm");

    // Check if time is after current time (if same date)
    if (isSameDate) {
      const now = moment().format("HH:mm");
      if (time24 < now) {
        return { isValid: false, time24 };
      }
    }

    // Check if time is after minimum time
    if (minTime && time24 < minTime) {
      return { isValid: false, time24 };
    }

    return { isValid: true, time24 };
  };

  // Handle blur event - format incomplete time
  const handleBlur = () => {
    if (inputValue && inputValue.length < 5) {
      const formattedTime = formatIncompleteTime(inputValue);
      setInputValue(formattedTime);

      if (formattedTime.length === 5) {
        const validation = validateTime(formattedTime, period);

        if (!validation.isValid) {
          // If time is invalid, set to current time or minimum time
          if (isSameDate) {
            const nowMoment = moment();
            setInputValue(nowMoment.format("hh:mm"));
            setPeriod(nowMoment.format("A") as "AM" | "PM");
            onChange(nowMoment.format("HH:mm"));
          } else if (minTime) {
            const minMoment = moment(minTime, "HH:mm");
            setInputValue(minMoment.format("hh:mm"));
            setPeriod(minMoment.format("A") as "AM" | "PM");
            onChange(minMoment.format("HH:mm"));
          }
          return;
        }

        onChange(validation.time24);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasUserInput.current = true;
    let value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length === 0) {
      setInputValue("");
      onChange("");
      return;
    }
    if (value.length > 2 && !value.includes(":")) {
      value = value.slice(0, 2) + ":" + value.slice(2);
    }
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    const parts = value.split(":");
    if (parts.length > 0) {
      let hours = parts[0];
      let minutes = parts[1] || "";

      // restrict hours to 01-12
      if (hours.length === 1 && parseInt(hours, 10) > 1) {
        hours = "0" + hours;
      }
      if (hours.length === 2 && parseInt(hours, 10) > 12) {
        hours = "12";
      }

      // restrict minutes
      if (minutes.length >= 1) {
        if (!["0", "1", "2", "3", "4", "5"].includes(minutes[0])) {
          minutes = "";
        }
      }
      if (minutes.length === 2) {
        if (!/[0-9]/.test(minutes[1])) {
          minutes = minutes[0];
        }
      }

      value = minutes.length > 0 ? `${hours}:${minutes}` : hours;
    }

    setInputValue(value);

    // Convert to 24hr & emit only if full HH:mm
    if (value.length === 5) {
      const validation = validateTime(value, period);

      if (!validation.isValid) {
        // If time is invalid, set to current time or minimum time
        if (isSameDate) {
          const nowMoment = moment();
          setInputValue(nowMoment.format("hh:mm"));
          setPeriod(nowMoment.format("A") as "AM" | "PM");
          onChange(nowMoment.format("HH:mm"));
        } else if (minTime) {
          const minMoment = moment(minTime, "HH:mm");
          setInputValue(minMoment.format("hh:mm"));
          setPeriod(minMoment.format("A") as "AM" | "PM");
          onChange(minMoment.format("HH:mm"));
        }
        return;
      }

      onChange(validation.time24);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData("text");
    const validTimePattern = /^(0[1-9]|1[0-2]):([0-5][0-9])$/;
    if (!validTimePattern.test(pasteData)) {
      e.preventDefault();
    }
  };

  const filteredTimes = generateTimeOptions().filter((time) => {
    if (!isSameDate) return true;

    const fullTime = moment(`${time} ${period}`, "hh:mm A").format("HH:mm");
    const now = moment().format("HH:mm");

    return fullTime > now;
  });

  const handlePeriodChange = (newPeriod: "AM" | "PM") => {
    // Validate the current time with the new period
    if (inputValue && inputValue.length === 5) {
      const validation = validateTime(inputValue, newPeriod);

      if (!validation.isValid) {
        // If time is invalid with the new period, set to current time
        if (isSameDate) {
          const nowMoment = moment();
          setInputValue(nowMoment.format("hh:mm"));
          setPeriod(nowMoment.format("A") as "AM" | "PM");
          onChange(nowMoment.format("HH:mm"));
          return;
        }
      }
    }

    setPeriod(newPeriod);
    if (inputValue.length === 5) {
      const m = moment(`${inputValue} ${newPeriod}`, "hh:mm A");
      onChange(m.format("HH:mm"));
    }
  };

  return (
    <>
      <Dropdown
        show={isOpen && filteredTimes?.length > 0}
        onToggle={(open) => setIsOpen(open && filteredTimes?.length > 0)}
        className="w-100 position-relative timeControl"
      >
        <FloatingLabel
          controlId="floatingTimePicker"
          label={
            <>
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </>
          }
          className="w-100"
        >
          <span
            className="material-icons material-symbols-outlined"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: disabled ? "not-allowed" : "pointer",
              color: disabled ? "#9f9898" : "#0092d5",
            }}
          >
            schedule
          </span>
          <Form.Control
            type="text"
            value={inputValue}
            placeholder={label}
            className="pe-5"
            onClick={() => setIsOpen(!isOpen)}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onPaste={handlePaste}
            disabled={disabled}
            maxLength={5}
            autoComplete="off"
          />
        </FloatingLabel>

        <Dropdown.Menu
          style={{ maxHeight: "200px", overflowY: "auto", width: "100%" }}
        >
          {filteredTimes.map((time) => (
            <Dropdown.Item
              key={time}
              onClick={() => {
                setInputValue(time);
                const m = moment(`${time} ${period}`, "hh:mm A");
                onChange(m.format("HH:mm"));
                setIsOpen(false);
              }}
            >
              {time}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>

        {inputValue && (
          <Dropdown
            onSelect={(val) => handlePeriodChange(val as "AM" | "PM")}
            className="custom-newtimepicker"
            style={{
              position: "absolute",
              top: "26px",
              left: "58px",
              border: "0px",
            }}
          >
            <Dropdown.Toggle variant="none" disabled={disabled}>
              {period}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                eventKey="AM"
                disabled={isSameDate && moment().hour() >= 12}
              >
                AM
              </Dropdown.Item>
              <Dropdown.Item eventKey="PM">PM</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </Dropdown>
    </>
  );
};

export default TimePicker;
